import { useAuth0 } from '@auth0/auth0-react';
import { resolveBrandIdentifierStem } from '@zealot/shared';
import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTokenRefresh } from '@/hooks/useTokenRefresh';
import {
  getAccessToken,
  getLoginMethod,
  getMagicToken,
  getRefreshToken,
  LOGIN_METHODS,
  removeLoginMethod,
  setAuthInProgress,
  setLoginMethod,
} from '@/lib/auth';
import { clearBrandSignedOut, markBrandSignedOut } from '@/lib/auth/brandSignOut';
import { buildLogoutOptions } from '@/lib/auth/buildLogoutOptions';
import { clearPreviousSignIn } from '@/lib/auth/clearPreviousSignIn';
import { clearSessionState, markJustLoggedOut } from '@/lib/auth/clearSessionState';
import { AUTH_TOKEN_CHANGED_EVENT } from '@/lib/auth/constants';
import { registerFetchToken } from '@/lib/auth/fetchTokenRegistry';
import { emailPasswordSignup, loginWithEmailAndPassword, startDbRedirect } from '@/lib/auth/loginMethods';
import { resolveToken } from '@/lib/auth/tokenResolver';
import { getHubBrand } from '@/store';
import { initiateSSOLogin } from '@/utils/authUtils';
import config from '@/utils/config';
import logger from '@/utils/logger';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { loginWithRedirect, isAuthenticated, getAccessTokenSilently, logout } = useAuth0();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Backfill login_method for legacy users who have tokens but no method stored
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hasTokens = !!getAccessToken() && !!getRefreshToken();
    const hasMethod = !!getLoginMethod();
    if (hasTokens && !hasMethod) {
      setLoginMethod(LOGIN_METHODS.SMS);
    }
  }, []);

  // A password sign-in never goes through the Auth0 SDK, so a session the SDK
  // still holds in memory belongs to whoever signed in through its redirect before. Left alone, the
  // landing page keeps registering and greeting that person. The SDK's own logout, kept local,
  // forgets them without leaving the page.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const forgetSdkSession = () => {
      const method = getLoginMethod();
      if (!method || method === LOGIN_METHODS.SERVICE || !isAuthenticated) return;
      logout({ openUrl: false });
    };
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, forgetSdkSession);
    return () => window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, forgetSdkSession);
  }, [isAuthenticated, logout]);

  const logoutUser = useCallback(
    async (explicit = false) => {
      if (typeof window === 'undefined') return;

      // Prevent re-renders from showing error screens while redirecting
      setIsLoggingOut(true);

      // Read before clearSessionState empties storage; written back after it, so leaving this hub
      // survives the Auth0 round trip and every refresh after it. The value becomes the hub's
      // subdomain in returnTo, through the same stem the admin dashboard's hub links use: the label
      // the person arrived on first, then the code, never a uuid-shaped one (an old link can still
      // bring someone in on the uuid host), and the name as the last resort.
      const hub = getHubBrand();
      const brand = resolveBrandIdentifierStem({ brandCode: hub.hubLabel ?? hub.code, brandName: hub.brandName });
      const signedOutBrandUuid = hub.uuid ?? null;
      clearSessionState();
      // Set AFTER clearSessionState() so it isn't wiped with the session. sessionStorage
      // persists across Auth0's redirect round-trip (same tab). Only mark on explicit
      // user-initiated logouts — implicit paths (token refresh failure, error recovery)
      // must not suppress SSO auto-login when the IdP session is still alive (ZLT-2936).
      // Only a person's own sign-out is a standing decision. A token refresh that failed, or an
      // error recovery, must not durably suppress sign-in — the same reason markJustLoggedOut is
      // gated here.
      if (explicit) {
        markJustLoggedOut();
        markBrandSignedOut(signedOutBrandUuid);
      }

      const env = process.env.NEXT_PUBLIC_ENVIRONMENT || 'local';
      const portalBaseUrl = config[env]?.portalBaseUrl;

      logout(buildLogoutOptions({ brand, origin: window.location.origin, portalBaseUrl }));
    },
    [logout],
  );

  const fetchToken = useCallback(
    async () =>
      resolveToken({
        getAccessTokenSilently,
        isAuthenticated,
        onLogout: logoutUser,
      }),
    [getAccessTokenSilently, isAuthenticated, logoutUser],
  );

  // Make fetchToken available to non-React code (legacy API wrappers via authFetch)
  useEffect(() => {
    registerFetchToken(fetchToken);
  }, [fetchToken]);

  // Background + visibility-based token refresh
  useTokenRefresh({ fetchToken });

  const authCredentialsAvailable = useCallback(() => {
    if (typeof window === 'undefined') return false;

    if (getMagicToken()) return true;

    const loginMethod = getLoginMethod();
    if (loginMethod === LOGIN_METHODS.SERVICE) return isAuthenticated;

    return !!(getAccessToken() && getRefreshToken());
  }, [isAuthenticated]);

  const handleLoginWithSSO = useCallback(
    async (brandName, { ssoConnectionName } = {}) => {
      try {
        // The redirect comes back as whoever the identity provider signs in, so nothing of the
        // previous person may survive the trip.
        clearPreviousSignIn();
        setAuthInProgress(true);
        // Choosing to sign in with SSO lifts a previous sign-out of this hub.
        clearBrandSignedOut(getHubBrand().uuid ?? null);
        setLoginMethod(LOGIN_METHODS.SERVICE);
        await initiateSSOLogin(loginWithRedirect, brandName, { ssoConnectionName });
      } catch (error) {
        setAuthInProgress(false);
        removeLoginMethod();
        logger.error(error, { context: 'loginWithSSO' });
        throw error;
      }
    },
    [loginWithRedirect],
  );

  const handleStartDbLogin = useCallback(() => startDbRedirect(loginWithRedirect, 'login'), [loginWithRedirect]);

  const handleStartDbSignup = useCallback(() => startDbRedirect(loginWithRedirect, 'signup'), [loginWithRedirect]);

  // checkUserExists is intentionally a no-op — probing Auth0 with wrong passwords
  // is a user-enumeration vulnerability. Callers should attempt login directly.
  const checkUserExists = async () => true;

  const contextValue = {
    fetchToken,
    logout: logoutUser,
    authCredentialsAvailable,
    loginWithSSO: handleLoginWithSSO,
    startDbLogin: handleStartDbLogin,
    startDbSignup: handleStartDbSignup,
    loginWithEmailAndPassword,
    emailPasswordSignup,
    checkUserExists,
  };

  // Render nothing while logout is in progress to prevent crash from cleared state
  if (isLoggingOut) return null;

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;

AuthProvider.propTypes = {
  children: PropTypes.node,
};
